import { forwardRef, ReactNode } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HelpIcon } from "@/components/shared/HelpIcon";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { ErrorTextComponent } from "../error-text-component";
import { HelperTextComponent } from "../helper-text-component";

export interface FormSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: ReactNode;
}

interface FormSelectProps {
  id?: string;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  options?: FormSelectOption[];
  placeholder?: string;
  readOnlyValue?: string;
  tooltip?: string;
  tooltipHtml?: ReactNode;
  disabled?: boolean;
  value?: string | number;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  allowClear?: boolean;
  className?: string;
  triggerClassName?: string;
}

export const FormSelect = forwardRef<HTMLButtonElement, FormSelectProps>(
  (
    {
      id,
      label,
      error,
      tooltip,
      required,
      helperText,
      options,
      placeholder,
      className,
      triggerClassName,
      readOnlyValue,
      tooltipHtml,
      disabled,
      value,
      onChange,
      onBlur,
      allowClear = false,
    },
    ref,
  ) => {
    // --- MODO LEITURA ---
    if (readOnlyValue) {
      return (
        <div className="space-y-2">
          <div className="flex h-[20px] items-center gap-2">
            {label && (
              <Label
                htmlFor={id}
                className={cn("text-sm font-medium", error && "text-destructive")}
              >
                {required && <span className="text-destructive">* </span>}
                {label}
              </Label>
            )}
            {(tooltip || tooltipHtml) && (
              <HelpIcon tooltipMessage={tooltip} tooltipHtml={tooltipHtml} />
            )}
          </div>
          <div className="block w-full rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
            {readOnlyValue}
          </div>
          <HelperTextComponent text={helperText} />
        </div>
      );
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.("");
    };

    return (
      <div className={cn("w-full space-y-2", className)}>
        {label && (
          <div className="flex h-[20px] items-center gap-2">
            {label && (
              <Label
                htmlFor={id}
                className={cn("text-base font-medium md:text-sm", error && "text-destructive")}
              >
                {required && <span className="text-destructive">* </span>}
                {label}
              </Label>
            )}
            {(tooltip || tooltipHtml) && (
              <HelpIcon tooltipMessage={tooltip} tooltipHtml={tooltipHtml} />
            )}
          </div>
        )}

        <Select disabled={disabled} value={value?.toString() || ""} onValueChange={onChange}>
          <SelectTrigger
            ref={ref}
            id={id}
            onBlur={onBlur}
            className={cn(
              "flex min-h-10 w-full items-center justify-between gap-2 bg-transparent px-3 dark:bg-input/30",
              error && "border-destructive focus:ring-destructive",
              triggerClassName,
            )}
          >
            <div className="min-w-0 flex-1 truncate text-left">
              <SelectValue
                placeholder={placeholder || "Selecione..."}
                className="text-base md:text-sm"
              >
                {(() => {
                  const selected = options?.find((opt) => String(opt.value) === String(value));
                  if (!selected) return null;
                  return (
                    <span className="flex min-w-0 items-center gap-2">
                      {selected.icon && (
                        <span className="shrink-0 text-muted-foreground">{selected.icon}</span>
                      )}
                      <span className="truncate">{selected.label}</span>
                    </span>
                  );
                })()}
              </SelectValue>
            </div>

            {allowClear && value && !disabled && (
              <div
                role="button"
                onClick={handleClear}
                className="rounded-sm p-0.5 transition-colors hover:bg-muted"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </div>
            )}
          </SelectTrigger>

          <SelectContent>
            {options?.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value.toString()}
                disabled={option.disabled}
                textValue={option.label}
                // Adicionado items-start para alinhar o ícone de check ao topo se o texto quebrar
                className="cursor-pointer items-start py-2"
              >
                {/* Adicionado w-full, min-w-0 e flex-1 para garantir o limite de largura */}
                <div className="flex w-full min-w-0 flex-1 flex-col gap-1 pr-2 text-left">
                  <span className="flex items-center gap-2 whitespace-normal break-words text-base font-medium leading-tight md:text-sm">
                    {option.icon && (
                      <span className="shrink-0 text-muted-foreground">{option.icon}</span>
                    )}
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="block w-full whitespace-normal break-words text-xs leading-snug text-muted-foreground opacity-80">
                      {option.description}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <ErrorTextComponent error={error} />
        <HelperTextComponent text={helperText} error={error} />
      </div>
    );
  },
);

FormSelect.displayName = "FormSelect";
